// apps/storefront/src/lib/analyticsVendors.ts
//
// Which vendor tags a tenant has configured, and what to do when a verdict
// changes. Pure and DOM-free so the load/revoke/reload rule is testable apart
// from the vendor bootstraps (which stay in AnalyticsScripts.tsx), and so the
// server can compute the same notice version the browser does. Ported from
// WOWsite's `apps/sites/src/components/analyticsVendors.ts`, minus GTM and chat.

import { VENDOR_ID_PATTERNS } from '@sifly/shared-utils'
import type { NoticeVendor } from './privacyDecision'

export type VendorPurpose = NoticeVendor
export const VENDOR_PURPOSES: readonly VendorPurpose[] = ['measurement', 'advertising', 'session_replay']

// Defense in depth: the portal validates IDs before storing, but these columns
// are plain TEXT, so any value that could reach a vendor URL is re-checked.
// The patterns are shared with the generated privacy disclosure, so the policy
// lists exactly the tags that can load here.
const { ga4: GA4_RE, metaPixel: PIXEL_RE, clarity: CLARITY_RE } = VENDOR_ID_PATTERNS

export type VendorIds = {
  ga4Id: string | null | undefined
  metaPixelId: string | null | undefined
  clarityId: string | null | undefined
}

export function safeIds(ids: VendorIds): Record<VendorPurpose, string | null> {
  return {
    measurement: ids.ga4Id && GA4_RE.test(ids.ga4Id) ? ids.ga4Id : null,
    advertising: ids.metaPixelId && PIXEL_RE.test(ids.metaPixelId) ? ids.metaPixelId : null,
    session_replay: ids.clarityId && CLARITY_RE.test(ids.clarityId) ? ids.clarityId : null,
  }
}

/** What this site actually runs — feeds the notice version. */
export function configuredVendors(ids: VendorIds): NoticeVendor[] {
  const s = safeIds(ids)
  return VENDOR_PURPOSES.filter((p) => s[p] !== null)
}

/**
 * Revoke through each vendor's own API immediately, then reload ONCE — and
 * only when a vendor that was actually running has just been denied. Client-
 * side routing means "stops on the next navigation" can mean a session
 * recorder runs for the rest of the visit; only a reload makes the scripts
 * absent. Denying only the first-party `analytics` purpose reloads nothing:
 * `track()` asks the engine on every call.
 */
export function planVendorChange({
  running,
  allowed,
}: {
  running: Record<VendorPurpose, boolean>
  allowed: Record<VendorPurpose, boolean>
}): { load: VendorPurpose[]; revoke: VendorPurpose[]; reload: boolean } {
  const load = VENDOR_PURPOSES.filter((p) => allowed[p] && !running[p])
  const revoke = VENDOR_PURPOSES.filter((p) => !allowed[p] && running[p])
  return { load, revoke, reload: revoke.length > 0 }
}

/**
 * The Consent Mode flags a running GA4 tag should carry, or null when no GA4
 * tag runs. Separate from planVendorChange because GA4 is the one vendor whose
 * flags can need changing while it keeps running (a Marketing opt-out on a
 * GA4-only tenant revokes nothing planVendorChange can see).
 *
 * The ad keys are granted only when advertising is allowed AND something that
 * advertises is configured, and never without measurement — they ride on the
 * same tag.
 */
export function gtagConsentFor({
  ga4Running,
  allowed,
  configured,
}: {
  ga4Running: boolean
  allowed: Record<VendorPurpose, boolean>
  configured: readonly VendorPurpose[]
}): { analytics: boolean; advertising: boolean } | null {
  if (!ga4Running) return null
  return {
    analytics: allowed.measurement,
    advertising: allowed.measurement && allowed.advertising && configured.includes('advertising'),
  }
}
